import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Upload, File, X, CheckCircle, AlertCircle, Download, Loader2 } from "lucide-react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import * as XLSX from "xlsx";
import newTemplate from "../../template/newTemplate_v1.1.xlsx";
import codTemplate from "../../template/newTemplate_v1.2_cod.xlsx";
import { parseCodAmount, COD_AMOUNT_RULE } from "../../utils/codAmount";
import { sellerEmailsErrorText } from "../../utils/createPackageErrors";
import { isCommuneCovered, normalizeCommune } from "../../utils/communes";
import { buildTemplateWithCoverage } from "../../utils/orderTemplate";
import "./MobileFirstFileInput.scss";

// Columna N de la plantilla v1.2 ("Cobrar"). El parser mapea por ÍNDICE, no por
// encabezado: la columna va al final justo por eso — insertarla en el medio correría
// todos los índices y el archivo se leería mal en silencio.
const COD_COLUMN_INDEX = 13;

// "Correo del vendedor": la única columna que se ubica por ENCABEZADO y no por
// índice fijo.
//
// El motivo es que su posición depende de la plantilla: va en N (13) en la v1.1 y
// en O (14) en la v1.2, que ya usa N para "Cobrar". Con un índice fijo harían falta
// cuatro binarios (COD sí/no × supervisor sí/no) y, peor, el parser no tendría forma
// de saber cuál subió el usuario: leer la columna equivocada atribuiría la venta a
// un correo que no es.
//
// Se ancla en `>= 13` para que no pueda ensombrecer ninguna de las columnas fijas
// (0..12): el bloque posicional que ya existía queda intacto.
const SELLER_EMAIL_MIN_INDEX = 13;
const SELLER_EMAIL_HEADER = /correo\s*(?:del\s*)?vendedor/i;

// La región de cada pedido se DERIVA de su comuna (`coverage.regionByCommune`).
//
// OJO con el nombre del campo: `shipping_address.city` NO guarda una ciudad,
// guarda la REGIÓN. Está así en microservice-packages, donde el filtro por
// región del portal lee `shipping_address.city.name`.
//
// Dos caminos ya probados y descartados, para que no se vuelvan a intentar:
//   - Un valor FIJO ("Santiago", después "Región Metropolitana"): deja los
//     pedidos fuera del filtro por región, y en Argentina les estampa la
//     región de otro país.
//   - VACIARLO: el DTO exige `@IsNotEmpty()` en `City.name`, así que el
//     ValidationPipe rechaza el LOTE COMPLETO con un 400 antes de llegar al
//     controller, y sin dejar rastro porque Nest no loguea 4xx.

/**
 * Ubica la columna del correo del vendedor en la fila de encabezados.
 * @returns índice 0-based, o -1 si el archivo no la trae.
 */
export const findSellerEmailColumn = (headerRow) => {
  if (!Array.isArray(headerRow)) return -1;
  for (let i = SELLER_EMAIL_MIN_INDEX; i < headerRow.length; i++) {
    if (SELLER_EMAIL_HEADER.test(String(headerRow[i] ?? "").trim())) return i;
  }
  return -1;
};

// Validación deliberadamente laxa: acá sólo se descartan cosas que no son un
// correo. Que el correo EXISTA y pertenezca al comercio lo resuelve el backend,
// que es el único que puede saberlo.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Función para obtener el país desde el número de teléfono
const getCountryFromPhone = (phone) => {
  const phoneStr = String(phone).trim();
  
  // Chile: +56, 56, o empieza con 9 (sin código de país)
  if (phoneStr.startsWith("+56") || phoneStr.startsWith("56") || /^9\d{8}$/.test(phoneStr)) {
    return "Chile";
  }
  // Argentina: +54 o 54
  if (phoneStr.startsWith("+54") || phoneStr.startsWith("54")) {
    return "Argentina";
  }
  // Brasil: +55 o 55
  if (phoneStr.startsWith("+55") || phoneStr.startsWith("55")) {
    return "Brasil";
  }
  // Estados Unidos: +1 o 1
  if (phoneStr.startsWith("+1") || phoneStr.startsWith("1")) {
    return "Estados Unidos";
  }
  
  return null;
};

// Función para validar número de teléfono según los requisitos
const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  
  const phoneStr = String(phone).trim().replace(/\s/g, "");
  
  // Chile: +56 seguido de 8-11 dígitos (puede incluir código de área)
  // Formatos válidos: +56912345678, +56212345678, +5656978778175, etc.
  // También acepta 569, 562, o empieza con 9 (8 dígitos después del 9)
  if (phoneStr.startsWith("+56")) {
    // +56 seguido de 8-11 dígitos adicionales (permite códigos de área como +562, +569)
    const digitsAfter56 = phoneStr.substring(3);
    if (/^\d{8,11}$/.test(digitsAfter56)) {
      return true;
    }
  }
  if (phoneStr.startsWith("56") && phoneStr.length >= 10) {
    // 56 seguido de 8-11 dígitos adicionales (sin el +)
    // Acepta 562, 569, etc.
    const digitsAfter56 = phoneStr.substring(2);
    if (/^\d{8,11}$/.test(digitsAfter56)) {
      return true;
    }
  }
  // Número que empieza con 9 seguido de 8 dígitos (formato local chileno)
  if (/^9\d{8}$/.test(phoneStr)) {
    return true;
  }
  
  // Argentina: +54 seguido de números
  if (phoneStr.startsWith("+54") || phoneStr.startsWith("54")) {
    return /^(\+?54)\d{8,10}$/.test(phoneStr);
  }
  
  // Brasil: +55 seguido de números
  if (phoneStr.startsWith("+55") || phoneStr.startsWith("55")) {
    return /^(\+?55)\d{10,11}$/.test(phoneStr);
  }
  
  // Estados Unidos: +1 seguido de números
  if (phoneStr.startsWith("+1") || phoneStr.startsWith("1")) {
    return /^(\+?1)\d{10}$/.test(phoneStr);
  }
  
  return false;
};

// `cod` = { enabled, currency } del operador que atiende a este comercio (lo resuelve
// el wizard contra el BFF). Con COD apagado la columna "Cobrar" se ignora por completo,
// aunque el archivo la traiga.
export default function MobileFirstFileInput({
  onSelectDocument,
  cod,
  // Supervisor de ventas: habilita la columna "Correo del vendedor". Para
  // cualquier otro usuario la columna se ignora aunque el archivo la traiga —
  // el backend aplica la misma regla y es el que manda.
  isSalesSupervisor = false,
  // Resuelve los correos contra el comercio del usuario. Inyectado por el wizard
  // para no acoplar este componente a la capa de API.
  resolveSellerEmails,
  // { names, index, regionByCommune } de las comunas que cubre el operador del
  // comercio, o null si no se pudo resolver.
  coverage,
  // "loading" | "ready" | "failed" | "no-coverage" | "unresolvable". Se separa de `coverage`
  // porque durante el arranque ambos son null-ish y confundirlos mostraría el
  // bloqueo mientras todavía se está resolviendo.
  //
  // Con "failed", "no-coverage" y "unresolvable" NO se deja cargar el archivo. Sin cobertura
  // no hay forma de saber la región de cada comuna, y habría que inventarla: un
  // pedido con la región equivocada no se nota al cargarlo, se nota tres pasos
  // después en la cadena. Decisión del dueño de producto (2026-08-27).
  //
  // La diferencia entre los dos es el aviso, no la compuerta: "no-coverage" es
  // un estado permanente (la empresa no tiene contrato activo o su operador
  // nunca declaró cobertura) y no gana nada reintentando, así que ahí no se
  // sugiere reintentar. "unresolvable" tampoco: la cobertura trae comunas que
  // el catálogo de regiones no ubica, y eso lo revisa la empresa de logística.
  coverageStatus = "ready",
  // Motivo que dio el backend cuando la cobertura falló ("failed"), p. ej. un
  // 403 explicado. Reemplaza el texto de "reintenta"; null si no lo hay.
  coverageError = null,
}) {
  const { t } = useTranslation();
  const codEnabled = !!cod?.enabled;
  const codCurrency = cod?.currency || "";
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  // Vendedores válidos a los que les falta la marca o la cuenta bancaria. Es un
  // aviso, no un error: no impide crear los pedidos.
  const [sellerWarnings, setSellerWarnings] = useState([]);
  const [isBuildingTemplate, setIsBuildingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  const coverageBlocked =
    coverageStatus === "failed" ||
    coverageStatus === "no-coverage" ||
    coverageStatus === "unresolvable";
  // El aviso dice qué hacer, no sólo que falló. En "failed" casi siempre es un
  // catálogo que no respondió y se arregla reintentando, salvo que el backend
  // haya dicho el motivo (se muestra tal cual). En "no-coverage" y en
  // "unresolvable" no hay nada que reintentar: falta una configuración que sólo
  // puede hacer (o revisar) la empresa de logística.
  let coverageBlockedTitle;
  let coverageBlockedText;
  if (coverageStatus === "no-coverage") {
    coverageBlockedTitle = t("Tu cuenta aún no tiene cobertura configurada");
    coverageBlockedText = t("Tu empresa de logística aún no tiene cobertura configurada para tu cuenta. Pídele que configure tu contrato y cobertura.");
  } else if (coverageStatus === "unresolvable") {
    coverageBlockedTitle = t("No podemos ubicar las comunas de tu cobertura");
    coverageBlockedText = t("Algunas comunas que cubre tu empresa de logística no tienen una región asignada, y la carga masiva la necesita para cada pedido. Avísale a tu empresa de logística o a soporte para que revisen la cobertura.");
  } else {
    coverageBlockedTitle = t("No podemos validar la cobertura ahora");
    coverageBlockedText =
      coverageError ||
      t("La carga masiva necesita saber qué comunas cubre tu empresa de logística para asignar la región de cada pedido. Vuelve a intentar en unos minutos; si sigue igual, avísale a soporte.");
  }

  /**
   * Región de una comuna, según la cobertura del operador.
   *
   * Devuelve undefined si no la encuentra, y el llamador lo trata como error de
   * fila: es preferible que el archivo no entre a que entre con la región mal.
   */
  const regionDeComuna = useCallback(
    (comuna) => coverage?.regionByCommune?.get(normalizeCommune(comuna)),
    [coverage],
  );

  // useCallback porque ahora depende de codEnabled: processFile la usa como dependencia
  // y sin memoizar se recrearía en cada render, invalidando ese callback.
  // `sellerColumn` es -1 cuando el archivo no trae la columna o el usuario no es
  // supervisor; en ese caso la venta queda de quien carga, igual que siempre.
  const mapRowToPackage = useCallback((row, rowIndex, sellerColumn = -1) => {
    const country = getCountryFromPhone(String(row[3]));
    const packageType = "EXCEL";

    // Comentario del envío (campo opcional, columna 7 según especificación)
    const getComments = (row) => {
      // row[7] es el comentario del envío
      return row[7] || null;
    };

    // Extraer dirección y departamento
    const addressLine = String(row[4] || "").trim();
    const streetParts = addressLine.split(",");
    const streetName = streetParts[0] || "";
    const streetNumber = streetParts[1] ? streetParts[1].trim() : "";
    
    // Dpto es opcional (columna 5 según especificación)
    const dpto = row[5] ? String(row[5]).trim() : "";

    const packageData = {
      type: packageType,
      receiver_name: String(row[2] || "").trim(),
      receiver_lastName: "",
      receiver_phone: String(row[3] || "").trim(),
      comments: getComments(row),
      packageDimensions: {
        height: row[8] ? parseFloat(row[8]) : null,
        width: row[9] ? parseFloat(row[9]) : null,
        length: row[10] ? parseFloat(row[10]) : null,
        weight: row[11] ? parseFloat(row[11]) : null,
      },
      shipping_address: {
        street_name: streetName,
        street_number: streetNumber,
        comment: dpto ? `Dpto: ${dpto}` : null,
        lat: null, // Las coordenadas serán obtenidas por el backend
        lng: null, // Las coordenadas serán obtenidas por el backend
        // La plantilla no trae columna de región: se DERIVA de la comuna, que
        // sí trae. `shipping_address.city` guarda la REGIÓN pese al nombre del
        // campo — está así en microservice-packages, donde el filtro por región
        // del portal lee `shipping_address.city.name`.
        //
        // Antes acá había un valor fijo. Eso dejaba a todos los pedidos por
        // planilla fuera del filtro por región y, en Argentina, con la región de
        // otro país. Vaciarlo tampoco sirve: el DTO exige `@IsNotEmpty()` y el
        // ValidationPipe rechaza el LOTE COMPLETO con un 400 que Nest no loguea.
        //
        // El lookup siempre acierta: una comuna fuera de cobertura ya frena la
        // fila en la validación, y sin cobertura resuelta no se llega a
        // procesar el archivo. El `?? ""` es la red de la red.
        city: {
          name: regionDeComuna(row[6]) ?? "",
        },
        municipality: {
          name: String(row[6] || "").trim(),
        },
        country: {
          name: country || "Chile",
        },
        delivery_preference: String(row[0] || "").toLowerCase(),
        address_line: addressLine,
      },
      status: 0,
      additionalLabels: row[12] ? parseInt(row[12]) : null,
    };

    // Número de venta (campo opcional, columna 1)
    if (row[1]) {
      packageData.order_id = String(row[1]).trim();
    }

    // Monto a cobrar contra entrega (campo opcional, columna N). Va en la RAÍZ del
    // paquete, no dentro de shipping_address. Si la celda está vacía no se agrega la
    // clave: su ausencia es lo que le dice al backend que el pedido no lleva cobro.
    if (codEnabled) {
      const codAmount = parseCodAmount(row[COD_COLUMN_INDEX]);
      if (codAmount.status === "ok") {
        packageData.cashOnDelivery = { amount: codAmount.amount };
      }
    }

    // Correo del vendedor. Viaja el CORREO y no un id: el backend lo vuelve a
    // resolver contra el comercio del token, así que nada de lo que se mande desde
    // acá decide a quién se le atribuye la venta. Si la celda está vacía la clave
    // se omite y el vendedor termina siendo quien carga.
    if (sellerColumn >= 0) {
      const sellerEmail = String(row[sellerColumn] ?? "").trim().toLowerCase();
      if (sellerEmail) packageData.sellerEmail = sellerEmail;
    }

    return packageData;
  }, [codEnabled, regionDeComuna]);

  const processFile = useCallback(async (file) => {
    setIsProcessing(true);
    setValidationErrors([]);
    setShowErrorModal(false);
    setProgressMessage(t("Procesando archivo..."));
    setIsSuccess(false);
    setSellerWarnings([]);

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Con pago contra entrega activo, la plantilla esperada es la v1.2, que
        // trae la columna "Cobrar" en N. Si el comercio sube la v1.1 (o cualquier
        // archivo sin esa columna), el parser —que mapea por ÍNDICE— simplemente
        // no encuentra row[13] y crea TODOS los pedidos sin cobro, sin un solo
        // aviso. Es plata que después nadie va a cobrar y que nadie va a notar
        // hasta que el repartidor entregue.
        //
        // Se corta acá, antes de procesar nada. Los encabezados están en la fila
        // 2 (índice 1); el parser nunca los lee, así que ésta es la única
        // validación de forma del archivo que existe.
        if (codEnabled) {
          const headerRow = rows[1] || [];
          const codHeader = String(headerRow[COD_COLUMN_INDEX] || "").trim();

          if (!codHeader) {
            setValidationErrors([{
              row: 0,
              errors: [
                t('Este archivo no tiene la columna "Cobrar". Tu empresa de logística ' +
                'tiene activado el pago contra entrega, así que hay que usar la ' +
                'plantilla nueva: descárgala con el botón de arriba y vuelve a ' +
                'cargar el archivo. Si continuaras con esta plantilla, los pedidos ' +
                'se crearían sin monto a cobrar.'),
              ],
            }]);
            setShowErrorModal(true);
            setProgressMessage("");
            // isProcessing lo apaga el `finally` de este try.
            return;
          }
        }

        // Mismo razonamiento que el guard de "Cobrar", y por el mismo motivo: si
        // un supervisor sube una plantilla vieja, la columna no aparece, cada
        // venta queda atribuida a él y —con cobro contra entrega— el repartidor
        // termina transfiriendo a la cuenta equivocada. Falla ruidoso acá o falla
        // mudo en la calle.
        const sellerColumn = isSalesSupervisor
          ? findSellerEmailColumn(rows[1] || [])
          : -1;

        if (isSalesSupervisor && sellerColumn === -1) {
          setValidationErrors([{
            row: 0,
            errors: [
              t('Este archivo no tiene la columna "Correo del vendedor". Como ' +
              'supervisor, tu plantilla la necesita para asignarle cada venta a su ' +
              'vendedor: descárgala con el botón de arriba y vuelve a cargar el ' +
              'archivo. Si continuaras, todas las ventas quedarían a tu nombre.'),
            ],
          }]);
          setShowErrorModal(true);
          setProgressMessage("");
          return;
        }

        let validRows = [];
        let hasError = false;
        const validationErrors = [];

        setProgressMessage(t("Validando datos del archivo..."));

        for (let i = 2; i < rows.length; i++) {
          const row = rows[i];

          // Si la primera columna está vacía, asumimos que no hay más filas
          if (!row[0]) break;

          const rowNumber = i + 1;
          const rowErrors = [];

          // Validar Tipo de encomienda (obligatorio, case-insensitive)
          setProgressMessage(t("Validando fila {{rowNumber}}: Tipo de encomienda...", { rowNumber }));
          const tipoEncomienda = String(row[0] || "").trim().toLowerCase();
          if (!tipoEncomienda || !["sameday", "nextday"].includes(tipoEncomienda)) {
            rowErrors.push(t('Tipo de encomienda debe ser "sameday" o "nextday" (sin importar mayúsculas/minúsculas). Valor encontrado: "{{value}}"', { value: row[0] || "(vacío)" }));
          }

          // Número de venta es opcional, no se valida

          // Validar Nombre del destinatario (obligatorio)
          setProgressMessage(t("Validando fila {{rowNumber}}: Nombre del destinatario...", { rowNumber }));
          if (!row[2] || !String(row[2]).trim()) {
            rowErrors.push(t("Nombre del destinatario es obligatorio y no puede estar vacío"));
          }

          // Validar Teléfono cliente (obligatorio)
          setProgressMessage(t("Validando fila {{rowNumber}}: Teléfono cliente...", { rowNumber }));
          if (!row[3] || !String(row[3]).trim()) {
            rowErrors.push(t("Teléfono cliente es obligatorio y no puede estar vacío"));
          } else if (!isValidPhoneNumber(row[3])) {
            const phoneStr = String(row[3]).trim();
            rowErrors.push(
              t('Teléfono cliente inválido. Debe ser un número válido de Chile (+56 seguido de 8-11 dígitos, +562, +569, 562, 569, o 9 seguido de 8 dígitos), ' +
              'Argentina (+54), Brasil (+55) o Estados Unidos (+1). Valor encontrado: "{{value}}"', { value: phoneStr })
            );
          }

          // Validar Dirección (obligatorio)
          setProgressMessage(t("Validando fila {{rowNumber}}: Dirección...", { rowNumber }));
          if (!row[4] || !String(row[4]).trim()) {
            rowErrors.push(t("Dirección es obligatoria y no puede estar vacía"));
          }

          // Dpto es opcional, no se valida

          // Validar Comuna (obligatorio) y, si se pudo resolver la cobertura del
          // operador, que además esté dentro de ella. Hasta ahora sólo se
          // comprobaba que no estuviera vacía: una comuna fuera de cobertura se
          // creaba igual y el problema aparecía recién en la operación.
          //
          // El cotejo es insensible a mayúsculas y tildes porque la colección
          // tiene "Ñuñoa", "NUÑOA" y "Nunoa" conviviendo.
          setProgressMessage(t("Validando fila {{rowNumber}}: Comuna...", { rowNumber }));
          if (!row[6] || !String(row[6]).trim()) {
            rowErrors.push(t("Comuna es obligatoria y no puede estar vacía"));
          } else if (!isCommuneCovered(row[6], coverage?.index)) {
            rowErrors.push(
              t('Tu empresa de logística no cubre la comuna "{{value}}". Revisa la hoja "Comunas" de la plantilla para ver la cobertura disponible.', { value: String(row[6]).trim() })
            );
          } else if (!regionDeComuna(row[6])) {
            // No debería ocurrir: la cobertura sólo se da por resuelta cuando
            // TODAS sus comunas tienen región. Si igual pasa, frena la fila en
            // vez de dejar que el pedido entre con la región vacía y se lleve
            // por delante el lote completo con un 400 mudo del ValidationPipe.
            rowErrors.push(
              t('No pudimos determinar la región de la comuna "{{value}}". Vuelve a intentar en unos minutos.', { value: String(row[6]).trim() })
            );
          }

          // Validar Monto a cobrar (opcional, columna N). Sólo aplica si el operador
          // cobra contra entrega; si está apagado la columna no se lee ni se valida.
          if (codEnabled) {
            setProgressMessage(t("Validando fila {{rowNumber}}: Monto a cobrar...", { rowNumber }));
            const codAmount = parseCodAmount(row[COD_COLUMN_INDEX]);
            if (codAmount.status === "invalid") {
              rowErrors.push(
                t('Monto a cobrar inválido. {{rule}} Valor encontrado: "{{value}}"', { rule: COD_AMOUNT_RULE, value: row[COD_COLUMN_INDEX] })
              );
            }
          }

          // Validar Correo del vendedor (opcional; sólo si el usuario es
          // supervisor y el archivo trae la columna). Acá sólo se comprueba la
          // FORMA: que el correo exista y sea de este comercio lo resuelve el
          // backend más abajo, que es el único que puede saberlo.
          //
          // `?? ""` y no `|| ""`: si la celda es la última de la fila y está
          // vacía, SheetJS acorta el arreglo y el valor llega como `undefined`,
          // no como cadena vacía.
          if (sellerColumn >= 0) {
            const sellerEmail = String(row[sellerColumn] ?? "").trim();
            if (sellerEmail && !EMAIL_SHAPE.test(sellerEmail)) {
              rowErrors.push(
                t('Correo del vendedor inválido. Valor encontrado: "{{value}}"', { value: sellerEmail })
              );
            }
          }

          // Comentario del envío es opcional, no se valida
          // Alto, Ancho, Largo, Peso son opcionales, no se validan
          // Cantidad de bultos es opcional, no se valida

          if (rowErrors.length > 0) {
            validationErrors.push({
              row: rowNumber,
              errors: rowErrors
            });
            hasError = true;
            // Continuar validando otras filas para mostrar todos los errores
            continue;
          }

          // Se guarda junto al número de fila: la resolución de correos de más
          // abajo necesita poder decir EN QUÉ FILA está el correo que falló.
          validRows.push({ row, rowNumber });
        }

        // Si hay errores de validación, mostrar modal con errores
        if (hasError && validationErrors.length > 0) {
          setValidationErrors(validationErrors);
          setShowErrorModal(true);
          setProgressMessage("");
        }

        if (!hasError && validRows.length > 0) {
          // Resolución de los correos contra el equipo del comercio. Es un aviso
          // TEMPRANO, no la compuerta: el backend vuelve a resolver cada correo y
          // rechaza el lote entero si alguno no corresponde. Se hace acá porque
          // `insertPackages` no es transaccional — fallar a mitad de camino
          // dejaría creados los paquetes anteriores.
          if (sellerColumn >= 0 && typeof resolveSellerEmails === "function") {
            setProgressMessage(t("Verificando los correos de los vendedores..."));

            const emailsByRow = validRows
              .map(({ row, rowNumber }) => ({
                rowNumber,
                email: String(row[sellerColumn] ?? "").trim().toLowerCase(),
              }))
              .filter((entry) => entry.email);

            if (emailsByRow.length > 0) {
              const unique = [...new Set(emailsByRow.map((e) => e.email))];
              const { data, error } = await resolveSellerEmails(unique);

              if (error) {
                // El rechazo puede ser de negocio ("Solo un supervisor de
                // ventas puede atribuir pedidos a otro usuario.") y ese texto sí
                // le sirve al usuario; `sellerEmailsErrorText` lo extrae y
                // devuelve null cuando no hubo respuesta del servidor, que es
                // cuando corresponde hablar de la conexión.
                setValidationErrors([{
                  row: 0,
                  errors: [sellerEmailsErrorText(error) || t("No pudimos verificar los correos de los vendedores. Revisa tu conexión y vuelve a intentarlo.")],
                }]);
                setShowErrorModal(true);
                setProgressMessage("");
                return;
              }

              const resolved = new Map(
                (data || []).map((seller) => [seller.email, seller]),
              );
              const sellerErrors = [];

              for (const { rowNumber, email } of emailsByRow) {
                const seller = resolved.get(email);
                if (!seller) {
                  sellerErrors.push({
                    row: rowNumber,
                    errors: [t('El correo "{{email}}" no corresponde a ningún usuario de tu empresa.', { email })],
                  });
                }
              }

              if (sellerErrors.length > 0) {
                setValidationErrors(sellerErrors);
                setShowErrorModal(true);
                setProgressMessage("");
                return;
              }

              // Vendedor válido pero sin cuenta bancaria utilizable. NO bloquea:
              // el pedido se puede crear igual y el dato lo completa después el
              // propio vendedor desde Mi Cuenta. Sólo importa si el pedido lleva
              // cobro contra entrega, y entonces el repartidor vería "sin cuenta".
              const incompletos = [
                ...new Set(
                  emailsByRow
                    .map(({ email }) => resolved.get(email))
                    .filter((s) => s && (!s.isSeller || !s.hasCompleteBankData))
                    .map((s) => s.email),
                ),
              ];
              if (incompletos.length > 0) {
                setSellerWarnings(incompletos);
              }
            }
          }

          setProgressMessage(t("Generando paquetes..."));
          const packagesJSON = validRows.map(({ row }, index) =>
            mapRowToPackage(row, index + 2, sellerColumn)
          );

          // Sin volcar `packagesJSON`: son datos personales de los destinatarios
          // (nombre, teléfono, dirección) y ahora además los correos de los
          // vendedores, y esto termina en la consola del navegador.
          console.log("Enviando datos al wizard:", {
            file: !!file,
            packagesCount: packagesJSON.length,
            fileType: file.type,
            fileName: file.name,
          });
          onSelectDocument({ file, packagesJSON });
          setUploadedFile(file);
          setIsSuccess(true);
          setProgressMessage(t("Archivo procesado exitosamente. {{count}} envío(s) válido(s) listo(s) para crear.", { count: validRows.length }));
          // Ocultar el mensaje de progreso después de unos segundos para no ocupar espacio
          setTimeout(() => setProgressMessage(""), 5000);
        } else if (!hasError) {
          setValidationErrors([{
            row: 0,
            errors: [t("No se encontraron registros válidos a partir de la fila 3. Por favor, verifique que el archivo tenga datos en las filas correspondientes.")]
          }]);
          setShowErrorModal(true);
          setProgressMessage("");
        }
      } catch (error) {
        setValidationErrors([{
          row: 0,
          errors: [t("Error al procesar el archivo. Verifica que sea un archivo Excel válido.")]
        }]);
        setShowErrorModal(true);
        setProgressMessage("");
      } finally {
        setIsProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
    // codEnabled entra en las dependencias porque decide si la columna N se valida:
    // con un callback obsoleto un archivo con montos se procesaría sin cobro.
    // Por lo mismo entran isSalesSupervisor y resolveSellerEmails: deciden si la
    // columna del vendedor se lee y contra qué comercio se resuelve.
  }, [onSelectDocument, codEnabled, mapRowToPackage, t, isSalesSupervisor, resolveSellerEmails, coverage, regionDeComuna]);

  const handleFileChange = useCallback((file) => {
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv"
    ];

    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setValidationErrors([{
        row: 0,
        errors: [t("Por favor selecciona un archivo Excel (.xlsx, .xls) o CSV válido")]
      }]);
      setShowErrorModal(true);
      return;
    }

    processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  }, [handleFileChange]);

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setValidationErrors([]);
    setShowErrorModal(false);
    setProgressMessage("");
    setIsSuccess(false);
    setSellerWarnings([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Entrega la plantilla con la cobertura del operador en el desplegable de
   * "Comuna". Sólo intercepta el click cuando hay cobertura resuelta; si no la
   * hay, deja que el <a> haga lo de siempre y baje el binario del repo.
   *
   * Cualquier fallo del parcheo también cae al binario: es preferible una
   * plantilla con la lista genérica que ningún archivo.
   */
  const handleDownloadTemplate = async (event) => {
    const communes = coverage?.names;
    if (!communes || communes.length === 0 || isBuildingTemplate) return;

    event.preventDefault();
    setIsBuildingTemplate(true);

    const templateUrl = codEnabled ? codTemplate : newTemplate;
    const fileName = codEnabled
      ? "plantilla-pedidos-cod.xlsx"
      : "plantilla-pedidos.xlsx";

    let objectUrl;
    try {
      const blob = await buildTemplateWithCoverage(templateUrl, communes);
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // Fallback explícito al asset del repo.
      window.location.href = templateUrl;
    } finally {
      // Se revoca en el próximo tick: revocarlo en el mismo bloque puede
      // cancelar la descarga que el click acaba de iniciar.
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      setIsBuildingTemplate(false);
    }
  };

  return (
    <div className="mobile-first-file-input">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileInputChange}
        className="mobile-first-file-input__hidden-input"
        aria-label={t("Seleccionar archivo Excel")}
      />

      <div className="mobile-first-file-input__container">
        {coverageBlocked ? (
          /*
           * Sin cobertura resuelta no se conoce la región de cada comuna, y la
           * región es obligatoria en el pedido. Antes se inventaba con un valor
           * fijo; ahora se prefiere que el archivo no entre. El título y el
           * texto salen de `coverageStatus` (arriba): el mismo bloqueo con dos
           * causas que se arreglan distinto.
           */
          <div
            className="mobile-first-file-input__dropzone"
            role="alert"
            aria-live="polite"
          >
            <div className="mobile-first-file-input__content">
              <div className="mobile-first-file-input__icon">
                <AlertCircle />
              </div>
              <h3 className="mobile-first-file-input__title">
                {coverageBlockedTitle}
              </h3>
              <p className="mobile-first-file-input__subtitle">
                {coverageBlockedText}
              </p>
            </div>
          </div>
        ) : !uploadedFile ? (
          <div
            className={`mobile-first-file-input__dropzone ${isDragOver ? "drag-over" : ""} ${isProcessing ? "processing" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={t("Área de carga de archivos. Arrastra un archivo Excel aquí o haz clic para seleccionar")}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }}
          >
            <div className="mobile-first-file-input__content">
              <div className="mobile-first-file-input__icon">
                {isProcessing ? (
                  <Loader2 className="mobile-first-file-input__spinner" />
                ) : (
                  <Upload size={24} />
                )}
              </div>
              
              <div className="mobile-first-file-input__text">
                <h4>{t("Arrastra tu archivo Excel aquí")}</h4>
                <p>{t("o haz clic para seleccionar")}</p>
                <div className="mobile-first-file-input__formats">.xlsx, .xls, .csv</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`mobile-first-file-input__success ${isSuccess ? 'success' : ''}`}>
            <div className="mobile-first-file-input__file-info">
              <div className="mobile-first-file-input__file-icon">
                <File size={16} />
              </div>
              <div className="mobile-first-file-input__file-details">
                <h5>{uploadedFile.name}</h5>
                <p>{t("Archivo cargado exitosamente")}</p>
              </div>
              <button
                type="button"
                className="mobile-first-file-input__remove-btn"
                onClick={handleRemoveFile}
                aria-label={t("Eliminar archivo")}
              >
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {progressMessage && (
          <div className="mobile-first-file-input__progress" role="status" aria-live="polite">
            <div className="mobile-first-file-input__progress-icon">
              {isProcessing ? (
                <Loader2 className="mobile-first-file-input__spinner" />
              ) : (
                <CheckCircle size={12} />
              )}
            </div>
            <span>{progressMessage}</span>
          </div>
        )}

        {/* Aviso, no error: los pedidos se crean igual. Sólo importa si alguno
            lleva cobro contra entrega — ahí el repartidor vería "sin cuenta". */}
        {sellerWarnings.length > 0 && (
          <div className="mobile-first-file-input__warning" role="status" aria-live="polite">
            <AlertCircle size={14} />
            <span>
              {t(
                "Estos vendedores todavía no tienen datos bancarios completos: {{emails}}. Los pedidos se van a crear igual, pero si alguno se cobra contra entrega el repartidor no va a ver a qué cuenta transferir.",
                { emails: sellerWarnings.join(", ") },
              )}
            </span>
          </div>
        )}

      </div>

      <div className="mobile-first-file-input__download">
        {/* Dos plantillas: la v1.2 sólo se ofrece si el operador cobra contra entrega.
            Entregarla siempre invitaría a llenar una columna que se ignora.

            Con cobertura resuelta el archivo se parchea en el navegador para que
            el desplegable de "Comuna" liste las comunas de ESTE operador; si algo
            falla se entrega el binario tal cual, que es lo que se entregaba antes. */}
        <a
          href={codEnabled ? codTemplate : newTemplate}
          className="mobile-first-file-input__download-link"
          download={codEnabled ? "newTemplate_v1.2_cod.xlsx" : "newTemplate_v1.1.xlsx"}
          aria-label={t("Descargar plantilla de Excel")}
          onClick={handleDownloadTemplate}
        >
          {isBuildingTemplate ? (
            <Loader2 size={12} className="mobile-first-file-input__spinner" />
          ) : (
            <Download size={12} />
          )}
          {t("Descargar plantilla")}
        </a>
        {coverage?.names?.length > 0 && (
          <p className="mobile-first-file-input__download-hint">
            {t(
              "La columna Comuna sólo acepta las {{count}} comunas que cubre tu empresa de logística; la plantilla las trae en el desplegable.",
              { count: coverage.names.length },
            )}
          </p>
        )}
        {codEnabled && (
          <p className="mobile-first-file-input__download-hint">
            La columna <strong>Cobrar</strong> es opcional: déjala vacía si el pedido no se
            cobra al destinatario. Los montos se cobran
            {codCurrency ? ` en ${codCurrency}` : ""}.
          </p>
        )}
      </div>

      {/* Modal de errores de validación */}
      <Modal isOpen={showErrorModal} toggle={() => setShowErrorModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowErrorModal(false)}>
          <div className="d-flex align-items-center gap-2">
            <AlertCircle size={20} className="text-danger" />
            <span>{t("Errores en el archivo Excel")}</span>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            {validationErrors.some(err => err.row === 0) ? (
              <div>
                {validationErrors.map((err, index) => (
                  <div key={index} className="mb-3 p-3 border rounded" style={{ backgroundColor: "#fff5f5" }}>
                    <ul className="mb-0" style={{ paddingLeft: "1.5rem" }}>
                      {err.errors.map((error, errorIndex) => (
                        <li key={errorIndex} className="mb-1" style={{ color: "#721c24" }}>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p className="mb-3">
                  {t("Se encontraron errores en")} <strong>{validationErrors.length} fila(s)</strong> del archivo.{" "}
                  {t("Por favor, corrija los errores y vuelva a cargar el archivo.")}
                </p>
                <div className="error-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {validationErrors.map((err, index) => (
                    <div key={index} className="mb-3 p-3 border rounded" style={{ backgroundColor: "#fff5f5" }}>
                      <h6 className="mb-2 text-danger">
                        <strong>Fila {err.row}:</strong>
                      </h6>
                      <ul className="mb-0" style={{ paddingLeft: "1.5rem" }}>
                        {err.errors.map((error, errorIndex) => (
                          <li key={errorIndex} className="mb-1" style={{ color: "#721c24" }}>
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              setShowErrorModal(false);
              setValidationErrors([]);
              setUploadedFile(null);
              setIsSuccess(false);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          >
            {t("Entendido, volver a cargar")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
